import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Swal from "sweetalert2";

import {
  fetchFieldHarvests,
  createFieldHarvest,
} from "../../api/FieldHarvestDataCaptureApi";

import { fetchFieldValueAdders } from "../../api/FieldValueAdderProfileApi";
import { fetchGHSEs, fetchBays } from "../../api/ghseApi";
import { fetchVarieties } from "../../api/varietyApi";

import "../../styles/FieldHarvestDataCapture.css";

// ── Custom Searchable Dropdown ─────────────────────────────
const SearchableSelect = ({ 
  name, 
  value, 
  onChange, 
  options, 
  placeholder, 
  disabled, 
  required,
  getDisplayLabel,
  getSearchText
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const text = getSearchText ? getSearchText(opt) : getDisplayLabel(opt);
      return text.toLowerCase().includes(term);
    });
  }, [options, searchTerm, getDisplayLabel, getSearchText]);

  const selectedOption = options.find(opt => String(opt.id) === String(value));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setHighlightedIndex(0);
    }
  }, [isOpen]);

  const handleSelect = (opt) => {
    onChange({ target: { name, value: String(opt.id) } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key.length === 1) {
        setIsOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(i => Math.min(i + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm("");
        break;
    }
  };

  return (
    <div 
      className={`select-wrapper searchable-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
    >
      <div 
        className="select-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={`select-value ${!selectedOption ? 'placeholder' : ''}`}>
          {selectedOption ? getDisplayLabel(selectedOption) : placeholder}
        </span>
        <div className="select-arrow">▼</div>
      </div>
      
      {isOpen && (
        <div className="select-dropdown">
          <div className="select-search-box">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(0);
              }}
              placeholder="Type to search..."
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="select-options">
            {filteredOptions.length === 0 ? (
              <div className="select-no-results">No matches found</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <div
                  key={opt.id}
                  className={`select-option ${String(opt.id) === String(value) ? 'selected' : ''} ${idx === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(opt)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                >
                  {getDisplayLabel(opt)}
                </div>
              ))
            )}
          </div>
        </div>
      )}
      
      {/* Hidden native select for form validation and accessibility */}
      <select name={name} value={value} onChange={onChange} required={required} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: 0, width: 0 }}>
        <option value="">{placeholder}</option>
        {options.map(opt => (
          <option key={opt.id} value={opt.id}>{getDisplayLabel(opt)}</option>
        ))}
      </select>
    </div>
  );
};

const FieldHarvestDataCapture = () => {
  // ---------------- DATA ----------------
  const [valueAdders, setValueAdders] = useState([]);
  const [ghses, setGhses] = useState([]);
  const [bays, setBays] = useState([]);
  const [varieties, setVarieties] = useState([]);
  const [records, setRecords] = useState([]);

  // ---------------- FILTERED ----------------
  const [filteredValueAdders, setFilteredValueAdders] = useState([]);
  const [filteredBays, setFilteredBays] = useState([]);
  const [filteredVarieties, setFilteredVarieties] = useState([]);

  // ---------------- WIZARD STATE ----------------
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedGHSE, setSelectedGHSE] = useState(null);

  // ---------------- FORM ----------------
  const [form, setForm] = useState({
    ghse: "",
    vertical_section: null,
    value_adder: "",
    bay: "",
    variety: "",
    number_of_nets: "",
  });

  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeCard, setActiveCard] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isScrolled, setIsScrolled] = useState(false);
  const canvasRef = useRef(null);
  const headerRef = useRef(null);

  // ---------------- LOAD ----------------
  useEffect(() => {
    loadData();
    initParticleCanvas();
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const initParticleCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let particles = [];
    let animationId;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.5 + 0.1;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x > canvas.width) this.x = 0;
        if (this.x < 0) this.x = canvas.width;
        if (this.y > canvas.height) this.y = 0;
        if (this.y < 0) this.y = canvas.height;
      }
      draw() {
        ctx.fillStyle = `rgba(199, 62, 125, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    for (let i = 0; i < 50; i++) particles.push(new Particle());

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.update();
        p.draw();
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  };

  const loadData = async () => {
    try {
      const [vaData, ghseData, bayData, varietyData, harvestData] = await Promise.all([
        fetchFieldValueAdders(),
        fetchGHSEs(),
        fetchBays(),
        fetchVarieties(),
        fetchFieldHarvests(),
      ]);
      setValueAdders(vaData);
      setGhses(ghseData);
      setBays(bayData);
      setVarieties(varietyData);
      setRecords(harvestData);
    } catch (err) {
      console.error(err);
    }
  };

  // ---------------- HELPERS ----------------
  const getGHSEName = useCallback(() =>
    ghses.find((g) => g.id === parseInt(form.ghse))?.name || "",
  [ghses, form.ghse]);

  const getValueAdderName = useCallback(() =>
    valueAdders.find((v) => v.id === parseInt(form.value_adder))?.full_name || "",
  [valueAdders, form.value_adder]);

  const getBayName = useCallback(() =>
    bays.find((b) => b.id === parseInt(form.bay))?.name || "",
  [bays, form.bay]);

  const getVarietyName = useCallback(() =>
    varieties.find((v) => v.id === parseInt(form.variety))?.name || "",
  [varieties, form.variety]);

  // Helper: get GHSE name for a bay
  const getBayGHSELabel = useCallback((bay) => {
    const ghse = ghses.find((g) => g.id === bay.ghse);
    return ghse ? `${bay.name} — ${ghse.name}` : bay.name;
  }, [ghses]);

  // Helper: get display label for value adder
  const getValueAdderDisplayLabel = useCallback((va) => {
    return va.payroll_number 
      ? `${va.full_name} — ${va.payroll_number}` 
      : va.full_name;
  }, []);

  const isToday = (dateString) => {
    if (!dateString) return false;
    const recordDate = new Date(dateString);
    const today = new Date();
    return (
      recordDate.getDate() === today.getDate() &&
      recordDate.getMonth() === today.getMonth() &&
      recordDate.getFullYear() === today.getFullYear()
    );
  };

  const getFilteredRecords = useCallback(() => {
    if (!selectedGHSE) return [];
    return records.filter((r) => {
      const matchesGHSE = r.ghse === selectedGHSE.id || r.ghse_id === selectedGHSE.id || r.ghse_name === selectedGHSE.name;
      return matchesGHSE && isToday(r.captured_at);
    });
  }, [records, selectedGHSE]);

  const getPerformanceData = useCallback(() => {
    const filtered = getFilteredRecords();
    const grouped = filtered.reduce((acc, record) => {
      const name = record.value_adder_name || "Unknown";
      const stems = parseInt(record.total_stems) || 0;
      if (!acc[name]) acc[name] = { name, totalStems: 0, captures: 0 };
      acc[name].totalStems += stems;
      acc[name].captures += 1;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.totalStems - a.totalStems);
  }, [getFilteredRecords]);

  const getGHSEsByFarm = useCallback(() => {
    const grouped = ghses.reduce((acc, ghse) => {
      const farmName = ghse.farm_name || ghse.farm || "Unassigned";
      if (!acc[farmName]) acc[farmName] = [];
      acc[farmName].push(ghse);
      return acc;
    }, {});
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [ghses]);

  // ---------------- HANDLERS ----------------
  const locationSupportsSelectedLevel = (location, ghse, section) => {
    if (!ghse?.requires_upper_lower) return !location.vertical_section;
    return !location.vertical_section || location.vertical_section === "both" || location.vertical_section === section;
  };

  const getVarietiesForSelection = (bayId, ghseId, valueAdderId, section, ghse) => {
    const selectedAdder = valueAdders.find((adder) => Number(adder.id) === Number(valueAdderId));
    if (!selectedAdder || !bayId) return [];
    const assignmentSection = ghse?.requires_upper_lower ? section : "single";
    const locationAssignment = selectedAdder.assignments?.length
      ? selectedAdder.assignments.find((assignment) => Number(assignment.bay_id ?? assignment.bay) === Number(bayId) && assignment.vertical_section === assignmentSection)
      : null;
    if (selectedAdder.assignments?.length && !locationAssignment) return [];
    const assignedVarieties = new Set((locationAssignment?.varieties || selectedAdder.varieties || []).map(Number));
    return varieties.filter((variety) => assignedVarieties.has(Number(variety.id)) &&
      (variety.locations_detail || []).some((location) =>
        Number(location.ghse_id) === Number(ghseId) &&
        Number(location.bay_id) === Number(bayId) &&
        locationSupportsSelectedLevel(location, ghse, section)
      )
    );
  };

  const promptVerticalSection = async (ghse, selected = null) => {
    const result = await Swal.fire({
      title: "Where was this bunch harvested?",
      text: `${ghse.name} has two capture levels. Choose one to continue.`,
      input: "radio",
      inputOptions: { upper: "Upper level", lower: "Lower level" },
      inputValue: selected,
      inputValidator: value => value ? undefined : "Select Upper or Lower to continue.",
      confirmButtonText: "Continue with this level",
      confirmButtonColor: "#c73e7d",
      customClass: { popup: "harvest-level-popup" },
      allowOutsideClick: false,
    });
    return result.isConfirmed ? result.value : null;
  };

  const handleGHSESelect = async (ghseId) => {
    const id = parseInt(ghseId);
    const ghse = ghses.find((g) => g.id === id);
    if (!ghse) return;

    const verticalSection = ghse.requires_upper_lower ? await promptVerticalSection(ghse) : null;
    if (ghse.requires_upper_lower && !verticalSection) return;

    setSelectedGHSE(ghse);

    // Filter value adders by GHSE
    const vaFiltered = valueAdders.filter((v) => v.ghses.includes(id));

    // Filter bays by GHSE upfront
    const bayFiltered = bays.filter((b) => b.ghse === id);

    // Filter varieties by GHSE upfront (using bay relationship)
    const varietyFiltered = varieties.filter((v) => {
      if (!v.locations_detail) return false;
      return v.locations_detail.some((loc) => {
        const locBayId = parseInt(loc.bay_id);
        const bay = bays.find((b) => b.id === locBayId);
        return bay && bay.ghse === id && locationSupportsSelectedLevel(loc, ghse, verticalSection);
      });
    });

    setFilteredValueAdders(vaFiltered);
    setFilteredBays(bayFiltered);
    setFilteredVarieties(varietyFiltered);

    setForm({ ghse: ghseId, vertical_section: verticalSection, value_adder: "", bay: "", variety: "", number_of_nets: "" });
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToGHSE = async () => {
    const result = await Swal.fire({
      title: "Change GHSE?",
      text: `You are currently working with ${selectedGHSE?.name}. All current form data will be lost.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#c73e7d",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Change",
      cancelButtonText: "Stay Here",
      reverseButtons: true,
    });
    if (result.isConfirmed) {
      setSelectedGHSE(null);
      setCurrentStep(1);
      setForm({ ghse: "", vertical_section: null, value_adder: "", bay: "", variety: "", number_of_nets: "" });
      setFilteredValueAdders([]);
      setFilteredBays([]);
      setFilteredVarieties([]);
    }
  };

  const handleChangeVerticalSection = async () => {
    if (!selectedGHSE?.requires_upper_lower) return;
    const verticalSection = await promptVerticalSection(selectedGHSE, form.vertical_section);
    if (verticalSection) {
      const eligible = getVarietiesForSelection(form.bay, form.ghse, form.value_adder, verticalSection, selectedGHSE);
      setFilteredVarieties(eligible);
      setForm(current => ({
        ...current,
        vertical_section: verticalSection,
        variety: eligible.some((variety) => Number(variety.id) === Number(current.variety)) ? current.variety : "",
      }));
    }
  };

  const handleValueAdderChange = (value) => {
    const selected = valueAdders.find((v) => v.id === parseInt(value));
    if (!selected) return;

    // Re-filter bays from already GHSE-filtered bays, then by value adder's assigned bays
    const ghseId = parseInt(form.ghse);
    const bayFiltered = bays.filter((b) => 
      b.ghse === ghseId && 
      selected.bays.includes(b.id)
    );

    setFilteredBays(bayFiltered);
    setFilteredVarieties([]);
    setForm((prev) => ({ ...prev, value_adder: value, bay: "", variety: "" }));
  };

  const handleBayChange = (value) => {
    const bayId = parseInt(value);
    const ghseId = parseInt(form.ghse);

    // Re-filter varieties from original varieties using BOTH ghse and bay conditions
    const filtered = getVarietiesForSelection(bayId, ghseId, form.value_adder, form.vertical_section, selectedGHSE);

    setFilteredVarieties(filtered);
    setForm((prev) => ({ ...prev, bay: value, variety: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "value_adder") return handleValueAdderChange(value);
    if (name === "bay") return handleBayChange(value);
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.ghse || !form.value_adder || !form.bay || !form.variety || !form.number_of_nets || (selectedGHSE?.requires_upper_lower && !form.vertical_section)) {
      Swal.fire({
        title: "Missing Fields",
        text: "Please fill all fields before submitting.",
        icon: "warning",
        confirmButtonColor: "#c73e7d",
      });
      return;
    }
    setShowConfirm(true);
  };

  const confirmSave = async () => {
    try {
      setLoading(true);
      await createFieldHarvest({ ...form, number_of_nets: parseInt(form.number_of_nets) });
      setForm((prev) => ({ ...prev, value_adder: "", bay: "", variety: "", number_of_nets: "" }));
      setFilteredValueAdders([]);
      setFilteredBays([]);
      setFilteredVarieties([]);
      setShowConfirm(false);
      const ghseId = parseInt(form.ghse);
      setFilteredValueAdders(valueAdders.filter((v) => v.ghses.includes(ghseId)));
      setFilteredBays(bays.filter((b) => b.ghse === ghseId));
      await loadData();
      Swal.fire({
        title: "Saved!",
        text: "Harvest data captured successfully.",
        icon: "success",
        confirmButtonColor: "#c73e7d",
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({ title: "Error", text: "Failed to save data.", icon: "error", confirmButtonColor: "#c73e7d" });
    } finally {
      setLoading(false);
    }
  };

  const handleMouseMove = (e) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  // ---------------- RENDER STEP 1 ----------------
  const renderStep1 = () => {
    const farms = getGHSEsByFarm();
    return (
      <div className="creative-root" onMouseMove={handleMouseMove}>
        <div className="hero-section">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="pulse-dot"></span>
              Live Harvest Tracking
            </div>
            <h1 className="hero-title">
              <span className="gradient-text">Field</span>
              <span className="outline-text">Harvest</span>
            </h1>
            <p className="hero-subtitle">Select your greenhouse to begin the harvest journey</p>
          </div>
          <div className="floating-shapes">
            <div className="shape shape-1" style={{ transform: `translate(${mousePos.x * 0.02}px, ${mousePos.y * 0.02}px)` }}></div>
            <div className="shape shape-2" style={{ transform: `translate(${mousePos.x * -0.015}px, ${mousePos.y * -0.015}px)` }}></div>
            <div className="shape shape-3" style={{ transform: `translate(${mousePos.x * 0.01}px, ${mousePos.y * 0.01}px)` }}></div>
          </div>
        </div>

        <div className="farms-orbit">
          {farms.map(([farmName, farmGhses], farmIdx) => (
            <div key={farmName} className="orbit-ring" style={{ animationDelay: `${farmIdx * 0.2}s` }}>
              <div className="orbit-header">
                <div className="orbit-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                </div>
                <h3 className="orbit-farm-name">{farmName}</h3>
                <span className="orbit-count">{farmGhses.length}</span>
              </div>
              <div className="ghse-constellation">
                {farmGhses.map((ghse, idx) => (
                  <button
                    key={ghse.id}
                    className={`ghse-planet ${activeCard === ghse.id ? 'active' : ''}`}
                    onClick={() => handleGHSESelect(ghse.id)}
                    onMouseEnter={() => setActiveCard(ghse.id)}
                    onMouseLeave={() => setActiveCard(null)}
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <div className="planet-core">
                      <div className="planet-glow"></div>
                      <span className="planet-emoji">🏠</span>
                    </div>
                    <div className="planet-info">
                      <span className="planet-name">{ghse.name}</span>
                      {ghse.zone_name && <span className="planet-zone">{ghse.zone_name}</span>}
                    </div>
                    <div className="planet-ring"></div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------------- RENDER STEP 2 ----------------
  const renderStep2 = () => (
    <div className="creative-root capture-mode" onMouseMove={handleMouseMove}>
      <div 
        ref={headerRef}
        className={`ghse-command-center ${isScrolled ? 'scrolled' : ''}`}
      >
        <div className="command-glass">
          <div className="command-avatar">
            <div className="avatar-core">
              <span>🏠</span>
            </div>
            <div className="avatar-pulse-ring"></div>
            <div className="avatar-pulse-ring delay"></div>
          </div>
          
          <div className="command-info">
            <div className="command-title-row">
              <h2 className="command-title">{selectedGHSE?.name}</h2>
              <div className="command-status">
                <span className="status-dot"></span>
                <span className="status-text">Live</span>
              </div>
            </div>
            <div className="command-meta-row">
              <span className="command-farm">{selectedGHSE?.farm_name}</span>
              <span className="command-separator">•</span>
              <span className="command-connection">Connected</span>
              {selectedGHSE?.requires_upper_lower && <><span className="command-separator">•</span><span className="capture-level-meta">{form.vertical_section === "upper" ? "UPPER LEVEL" : "LOWER LEVEL"}</span></>}
            </div>
          </div>

          <button className="command-switch" onClick={handleBackToGHSE}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M9 14L4 9l5-5"></path>
              <path d="M4 9h10.5a5.5 5.5 0 015.5 5.5v0a5.5 5.5 0 01-5.5 5.5H11"></path>
            </svg>
            <span>Switch</span>
          </button>
        </div>
      </div>

      <div className="header-spacer"></div>

      <div className="capture-grid">
        <div className="form-nebula">
          <div className="nebula-header">
            <div className="nebula-icon">✨</div>
            <h3>New Capture</h3>
          </div>
          
          <form onSubmit={handleSubmit} className="creative-form">
            {selectedGHSE?.requires_upper_lower && <div className={`capture-level-card is-${form.vertical_section}`}><span className="capture-level-icon" aria-hidden="true"><i>U</i><i>L</i></span><span className="capture-level-copy"><small>CAPTURE LEVEL</small><strong>{form.vertical_section === "upper" ? "Upper level" : "Lower level"}</strong><em>{form.vertical_section === "upper" ? "Top canopy" : "Lower canopy"}</em></span><span className="capture-level-status">ACTIVE</span><button type="button" onClick={handleChangeVerticalSection}>Change</button></div>}
            {/* Value Adder - Searchable Dropdown */}
            <div className="input-asteroid">
              <label className="input-label">
                <span className="label-icon">👷</span>
                Value Adder
              </label>
              <SearchableSelect
                name="value_adder"
                value={form.value_adder}
                onChange={handleChange}
                options={filteredValueAdders}
                placeholder="Select crew member..."
                required
                getDisplayLabel={getValueAdderDisplayLabel}
                getSearchText={(v) => `${v.full_name} ${v.payroll_number || ''}`}
              />
            </div>

            {/* Bay - Searchable Dropdown */}
            <div className="input-asteroid">
              <label className="input-label">
                <span className="label-icon">📍</span>
                Bay
              </label>
              <SearchableSelect
                name="bay"
                value={form.bay}
                onChange={handleChange}
                options={filteredBays}
                placeholder="Select bay..."
                disabled={!form.value_adder}
                required
                getDisplayLabel={getBayGHSELabel}
                getSearchText={getBayGHSELabel}
              />
            </div>

            {/* Variety - Standard Select (filtered by GHSE upfront, then by Bay) */}
            <div className="input-asteroid">
              <label className="input-label">
                <span className="label-icon">🌿</span>
                Variety
              </label>
              <div className="select-wrapper">
                <select name="variety" value={form.variety} onChange={handleChange} disabled={!form.bay} required>
                  <option value="">Select variety...</option>
                  {filteredVarieties.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} ({v.stems_per_net || 0} stems/net)</option>
                  ))}
                </select>
                <div className="select-arrow">▼</div>
              </div>
            </div>

            <div className="input-asteroid">
              <label className="input-label">
                <span className="label-icon">📦</span>
                Nets
              </label>
              <div className="number-input-galaxy">
                <button 
                  type="button" 
                  className="orbit-btn" 
                  onClick={() => setForm(p => ({...p, number_of_nets: Math.max(0, parseInt(p.number_of_nets || 0) - 1)}))}
                >−</button>
                <input 
                  type="number" 
                  name="number_of_nets" 
                  value={form.number_of_nets} 
                  onChange={handleChange} 
                  min="1" 
                  required 
                  placeholder="0"
                />
                <button 
                  type="button" 
                  className="orbit-btn" 
                  onClick={() => setForm(p => ({...p, number_of_nets: (parseInt(p.number_of_nets || 0) + 1)}))}
                >+</button>
              </div>
            </div>

            <button type="submit" className="launch-btn" disabled={loading}>
              <span className="launch-text">{loading ? "Processing..." : "Launch Capture"}</span>
              <div className="launch-trail"></div>
              <svg className="launch-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"></path>
              </svg>
            </button>
          </form>
        </div>

        <div className="data-universe">
          {renderPerformanceNebula()}
          {renderRecordsStream()}
        </div>
      </div>
    </div>
  );

  // ---------------- RENDER PERFORMANCE NEBULA ----------------
  const renderPerformanceNebula = () => {
    const performanceData = getPerformanceData();
    if (performanceData.length === 0) return null;

    const maxStems = Math.max(...performanceData.map(p => p.totalStems));
    
    return (
      <div className="nebula-card performance-nebula">
        <div className="nebula-header">
          <div className="nebula-icon">🌟</div>
          <h3>Performance Nebula</h3>
          <span className="nebula-time">{new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
        </div>
        <div className="star-chart">
          {performanceData.map((performer, index) => {
            const percentage = maxStems > 0 ? (performer.totalStems / maxStems) * 100 : 0;
            const rank = index + 1;
            return (
              <div key={performer.name} className="star-row" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="star-identity">
                  <div className={`star-rank rank-${rank <= 3 ? rank : 'other'}`}>
                    {rank === 1 ? '👑' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`}
                  </div>
                  <div className="star-details">
                    <span className="star-name">{performer.name}</span>
                    <span className="star-captures">{performer.captures} captures</span>
                  </div>
                </div>
                <div className="star-track">
                  <div className="star-orbit" style={{ width: `${percentage}%` }}>
                    <div className="star-comet"></div>
                    <span className="star-count">{performer.totalStems.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ---------------- RENDER RECORDS STREAM ----------------
  const renderRecordsStream = () => {
    const filteredRecords = getFilteredRecords();
    return (
      <div className="nebula-card records-stream">
        <div className="nebula-header">
          <div className="nebula-icon">📡</div>
          <h3>Live Stream</h3>
          <span className="stream-count">{filteredRecords.length} entries</span>
        </div>
        <div className="stream-container">
          {filteredRecords.length === 0 ? (
            <div className="empty-void">
              <div className="void-icon">🌑</div>
              <p>No signals from {selectedGHSE?.name} today</p>
            </div>
          ) : (
            filteredRecords.map((r, idx) => (
              <div key={r.id} className="stream-packet" style={{ animationDelay: `${idx * 0.05}s` }}>
                <div className="packet-time">
                  <div className="time-ring"></div>
                  <span>{new Date(r.captured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="packet-body">
                  <div className="packet-crew">
                    <div className="crew-avatar">{r.value_adder_name?.charAt(0) || '?'}</div>
                    <span className="crew-name">{r.value_adder_name}</span>
                  </div>
                  <div className="packet-location">
                    <span className="loc-bay">{r.bay_name}</span>
                    {r.vertical_section && <span className="loc-variety">{r.vertical_section === "upper" ? "Upper level" : "Lower level"}</span>}
                    <span className="loc-variety">{r.variety_name}</span>
                  </div>
                  <div className="packet-metrics">
                    <div className="metric-nets">
                      <span className="metric-value">{r.number_of_nets}</span>
                      <span className="metric-label">nets</span>
                    </div>
                    <div className="metric-stems">
                      <span className="metric-value highlight">{r.total_stems?.toLocaleString()}</span>
                      <span className="metric-label">stems</span>
                    </div>
                  </div>
                </div>
                <div className="packet-trail"></div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ---------------- CONFIRM MODAL ----------------
  const renderConfirmModal = () => {
    if (!showConfirm) return null;
    const varietyObj = varieties.find((v) => v.id === parseInt(form.variety));
    const stemsPerNet = varietyObj?.stems_per_net || 0;
    const totalStems = parseInt(form.number_of_nets || 0) * stemsPerNet;

    return (
      <div className="wormhole-overlay" onClick={() => setShowConfirm(false)}>
        <div className="wormhole-modal" onClick={(e) => e.stopPropagation()}>
          <div className="wormhole-header">
            <div className="wormhole-rings">
              <div className="ring ring-1"></div>
              <div className="ring ring-2"></div>
              <div className="ring ring-3"></div>
            </div>
            <h3>Confirm Transmission</h3>
            <button className="wormhole-close" onClick={() => setShowConfirm(false)}>✕</button>
          </div>

          <div className="transmission-data">
            <div className="data-beam">
              <div className="beam-label">Target GHSE</div>
              <div className="beam-value target">{selectedGHSE?.name}</div>
            </div>
              {selectedGHSE?.requires_upper_lower && <div className="data-beam"><div className="beam-label">Capture level</div><div className="beam-value accent">{form.vertical_section === "upper" ? "Upper" : "Lower"}</div></div>}
            
            <div className="data-grid">
              <div className="data-beam">
                <div className="beam-label">Crew</div>
                <div className="beam-value">{getValueAdderName()}</div>
              </div>
              <div className="data-beam">
                <div className="beam-label">Location</div>
                <div className="beam-value">{getBayName()}</div>
              </div>
              <div className="data-beam">
                <div className="beam-label">Species</div>
                <div className="beam-value">{getVarietyName()}</div>
              </div>
              <div className="data-beam">
                <div className="beam-label">Nets</div>
                <div className="beam-value accent">{form.number_of_nets}</div>
              </div>
            </div>

            <div className="data-beam total-beam">
              <div className="beam-label">Total Stems</div>
              <div className="beam-value mega">{totalStems.toLocaleString()}</div>
            </div>
          </div>

          <div className="wormhole-actions">
            <button className="abort-btn" onClick={() => setShowConfirm(false)}>Abort</button>
            <button className="transmit-btn" onClick={confirmSave} disabled={loading}>
              <span>{loading ? "Transmitting..." : "Transmit"}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ---------------- MAIN RENDER ----------------
  return (
    <div className="creative-universe">
      <canvas ref={canvasRef} className="particle-canvas"></canvas>
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {renderConfirmModal()}
    </div>
  );
};

export default FieldHarvestDataCapture;
